import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { OverlayScrollArea } from '../components/OverlayScrollbar'
import { providerClient } from '../provider/providerClient'
import type { ProviderProfile } from '../provider/providerClient'

/**
 * ORGANIZATION — the first screen, and the one that proves the whole chain: four-token signin →
 * `provider.*` → the organization resolved from the session's user row → data read from BOTH of
 * this portal's base sources (David, 2026-07-23: "it uses the supplied content and the Content as
 * its base source").
 *
 * ★★ THE TWO COUNTS ARE PRESENTED AS TWO ANSWERS, NOT AS A RECONCILIATION. "What you sent us" and
 * "what of yours is published" genuinely differ and not 1:1 in either direction, so they sit in
 * separate cards with their own words. A reader who expects them to match is being told something
 * the data cannot say — and the failure mode is trust, not cosmetics.
 *
 * ⚠ NO WATCH FIGURES HERE. Analytics arrive in D2/D3, on their own screens. A view count next to
 * a delivery would put a zero against everything a supplier has sent that we have not published
 * yet, which reads as "nobody watched my film".
 */

const wrap: CSSProperties = { padding: 24, maxWidth: 900 }
/** ★ Primary:VeryDark, not --foreground — see the note in `Overview.tsx`. */
const h1: CSSProperties = {
  fontSize: 'var(--fs-title)',
  fontWeight: 600,
  margin: '0 0 2px',
  color: 'var(--primary-verydark)',
}
const sub: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', margin: '0 0 22px' }
const cards: CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 22 }
const card: CSSProperties = {
  flex: '1 1 220px',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '14px 16px',
}
const stat: CSSProperties = { fontFamily: 'var(--font-number)', fontSize: 32, lineHeight: 1.1, fontWeight: 600 }
const statLabel: CSSProperties = { fontSize: 'var(--fs-sm)', fontWeight: 500, marginTop: 4 }
const statNote: CSSProperties = { fontSize: 'var(--fs-xs)', color: 'var(--muted-foreground)', marginTop: 6 }
const panel: CSSProperties = { ...card, flex: '1 1 100%', marginBottom: 12 }
const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: 12,
  padding: '5px 0',
  fontSize: 'var(--fs-sm)',
}
const key: CSSProperties = { color: 'var(--muted-foreground)' }
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const notice: CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--warning)',
  borderRadius: 'var(--radius)',
  padding: '14px 16px',
  maxWidth: 620,
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={rowStyle}>
      <span style={key}>{label}</span>
      <span>{children}</span>
    </div>
  )
}

export function Organization() {
  const [profile, setProfile] = useState<ProviderProfile | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    providerClient
      .profile()
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load your organization.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <div style={wrap}><div style={{ ...muted, color: 'var(--destructive)' }}>{error}</div></div>
  if (!profile) return <div style={wrap}><div style={muted}>Loading…</div></div>

  /**
   * ⚠ A NAMED STATE, NOT AN EMPTY DASHBOARD. An account nobody has linked to an organization yet
   * is shown what is actually true and who to tell — never a screen of zeros, which would say we
   * have nothing of theirs.
   */
  if (!profile.Linked || !profile.Organization) {
    const missing = profile.Reason === 'organization_missing'
    return (
      <OverlayScrollArea axis="y">
        <div style={wrap}>
          <h1 style={h1}>Organization</h1>
          <p style={sub}>Your account and what we hold of yours.</p>
          <div style={notice}>
            <p style={{ margin: '0 0 8px', fontWeight: 500 }}>
              {missing ? 'Your organization record is missing.' : 'Your account is not linked to an organization yet.'}
            </p>
            <p style={{ ...muted, margin: 0 }}>
              That is something we set up at our end — nothing is wrong with your sign-in. Email{' '}
              <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a> and we will
              sort it out.
            </p>
          </div>
        </div>
      </OverlayScrollArea>
    )
  }

  const org = profile.Organization
  const supplied = profile.Supplied ?? { Deliveries: 0, Items: 0 }
  const catalogue = profile.Catalogue ?? { Titles: 0, ByType: [] }

  return (
    <OverlayScrollArea axis="y">
      <div style={wrap}>
        <h1 style={h1}>{org.Name || org.PrimaryKey}</h1>
        <p style={sub}>Your account and what we hold of yours.</p>

        <div style={cards}>
          {/* Base source #1 — the delivery ledger, authored by us as things arrive. */}
          <div style={card}>
            <div style={stat}>{supplied.Deliveries}</div>
            <div style={statLabel}>{supplied.Deliveries === 1 ? 'delivery recorded' : 'deliveries recorded'}</div>
            <div style={statNote}>
              {supplied.Items} item{supplied.Items === 1 ? '' : 's'} in total, counting everything beneath them
            </div>
          </div>
          {/* Base source #2 — the catalogue, by the same attribution the watch data carries. */}
          <div style={card}>
            <div style={stat}>{catalogue.Titles}</div>
            <div style={statLabel}>{catalogue.Titles === 1 ? 'title in the catalogue' : 'titles in the catalogue'}</div>
            <div style={statNote}>
              {catalogue.ByType.length > 0
                ? catalogue.ByType.map((t) => `${t.Titles} ${t.EntryType ?? 'other'}`).join(' · ')
                : 'Nothing published yet'}
            </div>
          </div>
        </div>

        {/* ⚠ Said plainly, because the two numbers above invite exactly this question. */}
        <p style={{ ...muted, maxWidth: 620, marginTop: -10, marginBottom: 22 }}>
          These two counts are not meant to match: one series delivery can become several
          episodes, and a film's master, captions and artwork are three deliveries of one title.
        </p>

        <div style={panel}>
          <Row label="Status">{org.Status || '—'}</Row>
          {org.Website && (
            <Row label="Website">
              <a href={org.Website} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>
                {org.Website}
              </a>
            </Row>
          )}
          {org.Contacts.length > 0 && (
            <Row label={org.Contacts.length === 1 ? 'Contact' : 'Contacts'}>
              {org.Contacts.map((c, i) => (
                <div key={`${c.Email ?? c.Name ?? i}`}>
                  {c.Name}
                  {c.JobTitle ? ` · ${c.JobTitle}` : ''}
                  {c.Email ? ` · ${c.Email}` : ''}
                  {c.Primary ? ' · primary' : ''}
                </div>
              ))}
            </Row>
          )}
          {(profile.Accounts ?? []).length > 0 && (
            <Row label="Sign-ins">
              {(profile.Accounts ?? []).map((a) => (
                <div key={a.EmailAddress}>{a.DisplayName ? `${a.DisplayName} · ${a.EmailAddress}` : a.EmailAddress}</div>
              ))}
            </Row>
          )}
        </div>

        {/* ★ The whole write story (D9): the portal is read-only, so the way to change anything
            here is to tell us. A mailto is the entire mechanism, as it was in v1. */}
        <p style={muted}>
          Something not right?{' '}
          <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>Let us know</a> and we will correct it.
        </p>
      </div>
    </OverlayScrollArea>
  )
}
